<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore, useMapGetter, useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AgentsAPI from 'dashboard/api/agents';
import { PRESETS } from 'dashboard/helper/agentPermissionsCatalog';

const emit = defineEmits(['close']);

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const firstName = ref('');
const lastName = ref('');
const agentEmail = ref('');
const displayName = ref('');
const password = ref('');
const passwordConfirm = ref('');
const showPassword = ref(false);

// Rol base (espejo de los "Roles base" de Permisos) + sucursales + departamento.
const selectedRoleKey = ref('sales_agent');
const selectedBranchIds = ref([]);
const selectedDepartment = ref('');

const saving = ref(false);

const uiFlags = useMapGetter('agents/getUIFlags');
const branchList = computed(() => store.getters['branches/getBranches'] || []);
const getters = useStoreGetters();

const accountId = computed(() => route.params.accountId);
const currentAccount = computed(
  () => store.getters['accounts/getAccount'](accountId.value) || {}
);
const DEFAULT_DEPARTMENTS = ['Administración', 'Gerencia', 'Recepción', 'Video', 'Fotógrafo', 'Maquillistas'];
const departmentOptions = computed(() => [
  ...(currentAccount.value.custom_attributes?.agent_departments || DEFAULT_DEPARTMENTS),
  'Sin departamento',
]);

const allAgents = computed(() => getters['agents/getAgents'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const isCurrentUserSuperAdmin = computed(() => {
  const me = allAgents.value.find(a => a.id === currentUserId.value);
  return me?.super_admin === true;
});

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('branches/get');
});

const fullName = computed(() => `${firstName.value} ${lastName.value}`.trim());

const initials = computed(() => {
  const f = (firstName.value || '').trim()[0] || '';
  const l = (lastName.value || '').trim()[0] || '';
  return (f + l).toUpperCase() || '??';
});

const passwordMismatch = computed(
  () => password.value && passwordConfirm.value && password.value !== passwordConfirm.value
);

const canSubmit = computed(() => {
  if (!firstName.value.trim() || !agentEmail.value.trim()) return false;
  if (passwordMismatch.value) return false;
  if (password.value && password.value.length < 6) return false;
  return true;
});

const helpPasswordText = computed(() => {
  if (passwordMismatch.value) return 'Las contraseñas no coinciden.';
  if (password.value && password.value.length < 6) return 'Mínimo 6 caracteres.';
  if (!password.value) return '💡 Si dejas en blanco, se enviará un correo al agente con un link para que ponga su propia contraseña.';
  return '✓ La contraseña queda lista — el agente puede iniciar sesión inmediatamente.';
});

// Roles base: mismas plantillas que la página de Permisos (espejo de PRESETS).
// `cw` = rol Chatwoot real que se setea al CREAR; el resto del poder va en
// feature_permissions (igual que el modal de Permisos al aplicar la plantilla).
const ROLE_BASE = [
  { key: 'general_manager', cw: 'agent' },
  { key: 'sales_agent', cw: 'agent' },
  { key: 'secretaria', cw: 'agent' },
  { key: 'cuentas_cobrar', cw: 'agent' },
  { key: 'seleccion', cw: 'agent' },
  { key: 'fotografo', cw: 'agent' },
  { key: 'fotografo_retoque', cw: 'agent' },
  { key: 'impresion', cw: 'agent' },
  { key: 'encargada', cw: 'agent' },
  { key: 'read_only', cw: 'agent' },
  { key: 'branch_admin', cw: 'administrator' },
  { key: 'full_access', cw: 'super_admin' },
];
const baseRoleList = computed(() =>
  ROLE_BASE.filter(r => r.cw !== 'super_admin' || isCurrentUserSuperAdmin.value).map(r => ({
    ...r,
    label: PRESETS[r.key]?.label || r.key,
    desc: PRESETS[r.key]?.description || '',
  }))
);
const selectedRoleDef = computed(() => ROLE_BASE.find(r => r.key === selectedRoleKey.value));
const isSuperRole = computed(() => selectedRoleDef.value?.cw === 'super_admin');
const activeRoleDesc = computed(() => PRESETS[selectedRoleKey.value]?.description || '');

function pickRole(key) {
  selectedRoleKey.value = key;
  if (ROLE_BASE.find(r => r.key === key)?.cw === 'super_admin') selectedBranchIds.value = [];
}

function toggleBranch(branch) {
  if (branch.is_personal) return;
  if (isSuperRole.value) return;
  const idx = selectedBranchIds.value.indexOf(branch.id);
  if (idx >= 0) selectedBranchIds.value.splice(idx, 1);
  else selectedBranchIds.value.push(branch.id);
}

function isBranchSelected(branch) {
  return selectedBranchIds.value.includes(branch.id);
}

function branchClass(branch) {
  if (branch.is_personal) return 'pers';
  const code = (branch.code || '').toLowerCase();
  if (code === 'lrm') return 'lrm';
  if (code === 'sde') return 'sde';
  if (code === 'sdo') return 'sdo';
  if (code === 'stg') return 'stg';
  return 'other';
}

function pickDepartment(d) {
  selectedDepartment.value = d === 'Sin departamento' ? '' : d;
}

async function submit() {
  if (!canSubmit.value || saving.value) return;
  saving.value = true;
  try {
    const def = selectedRoleDef.value;
    const payload = {
      name: fullName.value,
      email: agentEmail.value.trim(),
      role: def.cw === 'agent' ? 'agent' : 'administrator',
    };
    if (def.cw === 'super_admin') payload.super_admin = true;
    if (displayName.value.trim()) payload.display_name = displayName.value.trim();
    if (password.value) payload.password = password.value;

    const response = await AgentsAPI.create(payload);
    const newAgent = response.data;

    // 2ª llamada (igual que el modal de Permisos): aplica el rol base como
    // feature_permissions + departamento + sucursales (la 1ª = principal).
    if (newAgent?.id) {
      const upd = {
        feature_permissions: PRESETS[selectedRoleKey.value].apply(),
        department: selectedDepartment.value,
      };
      if (def.cw !== 'super_admin' && selectedBranchIds.value.length > 0) {
        upd.accessible_branch_ids = selectedBranchIds.value;
        upd.primary_branch_id = selectedBranchIds.value[0];
      }
      try {
        await AgentsAPI.update(newAgent.id, { agent: upd });
      } catch (e) {
        useAlert('Agente creado, pero falló aplicar rol/sucursal/departamento. Ajústalo en Permisos.');
      }
    }

    await store.dispatch('agents/get');
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const msg = error?.response?.data?.error
      || error?.response?.data?.message
      || (error?.response?.status === 422
        ? t('AGENT_MGMT.ADD.API.EXIST_MESSAGE')
        : t('AGENT_MGMT.ADD.API.ERROR_MESSAGE'));
    useAlert(msg);
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="aa-modal">
    <div class="aa-head">
      <div class="aa-avatar">{{ initials }}</div>
      <div class="aa-head-info">
        <div class="aa-head-title">{{ fullName || 'Crear nuevo agente' }}</div>
        <div class="aa-head-sub">Datos · rol · sucursal · departamento</div>
      </div>
      <button class="aa-head-x" @click="emit('close')">×</button>
    </div>

    <div class="aa-body">
      <!-- Datos -->
      <div class="aa-sect">
        <div class="aa-section-title">Datos</div>
        <div class="aa-grid-2">
          <div class="aa-field">
            <div class="aa-field-label">Nombre <span class="aa-req">*</span></div>
            <input v-model="firstName" class="aa-input" placeholder="Ej: María" />
          </div>
          <div class="aa-field">
            <div class="aa-field-label">Apellido</div>
            <input v-model="lastName" class="aa-input" placeholder="Ej: Liz" />
          </div>
        </div>
        <div class="aa-field">
          <div class="aa-field-label">Correo <span class="aa-req">*</span></div>
          <input v-model="agentEmail" type="email" class="aa-input" placeholder="agente@empresa.com" />
        </div>
        <div class="aa-field">
          <div class="aa-field-label">Nombre de usuario <span class="aa-lbl-hint">· visible al cliente · opcional</span></div>
          <input v-model="displayName" class="aa-input" placeholder="ej: marializ" />
        </div>
        <div class="aa-field">
          <div class="aa-field-label">Contraseña</div>
          <div class="aa-pass-wrap">
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              class="aa-input aa-input-pass"
              placeholder="Opcional — en blanco envía link por correo"
            />
            <button type="button" class="aa-pass-toggle" @click="showPassword = !showPassword">
              {{ showPassword ? '🙈' : '👁' }}
            </button>
          </div>
          <input
            v-if="password"
            v-model="passwordConfirm"
            :type="showPassword ? 'text' : 'password'"
            class="aa-input aa-pass-confirm"
            :class="{ 'aa-input-error': passwordMismatch }"
            placeholder="Confirmar contraseña"
          />
          <div
            class="aa-pass-help"
            :class="{ 'aa-pass-error': passwordMismatch, 'aa-pass-ok': password && !passwordMismatch }"
          >{{ helpPasswordText }}</div>
        </div>
      </div>

      <!-- Rol base (los 12, igual que Permisos) -->
      <div class="aa-sect">
        <div class="aa-section-title">Rol base</div>
        <div class="aa-roles2">
          <button
            v-for="r in baseRoleList"
            :key="r.key"
            type="button"
            class="aa-role2"
            :class="{ on: selectedRoleKey === r.key }"
            @click="pickRole(r.key)"
          >{{ r.label }}</button>
        </div>
        <div class="aa-seg-hint">{{ activeRoleDesc }}</div>
      </div>

      <!-- Sucursales (la 1ª = principal · aparece en el Kanban) -->
      <div v-if="!isSuperRole" class="aa-sect">
        <div class="aa-section-title">Sucursales <span class="aa-count">{{ selectedBranchIds.length }}</span></div>
        <div class="aa-branches">
          <div
            v-for="branch in branchList"
            :key="`b-${branch.id}`"
            class="aa-branch-chip"
            :class="[branchClass(branch), { active: isBranchSelected(branch), disabled: branch.is_personal }]"
            @click="toggleBranch(branch)"
          >
            <div class="aa-branch-dot" :class="branchClass(branch)" />
            <div class="aa-branch-name">{{ branch.name?.toUpperCase() }}</div>
            <span v-if="selectedBranchIds[0] === branch.id" class="aa-primary-tag">principal</span>
          </div>
        </div>
        <div class="aa-branches-info">La 1ª sucursal seleccionada es la principal (aparece en el Kanban) · super admins ven todas</div>
      </div>

      <!-- Departamento (columna del Kanban de agentes) -->
      <div class="aa-sect">
        <div class="aa-section-title">Departamento</div>
        <div class="aa-branches">
          <div
            v-for="d in departmentOptions"
            :key="`d-${d}`"
            class="aa-branch-chip"
            :class="{ active: (selectedDepartment || 'Sin departamento') === d }"
            @click="pickDepartment(d)"
          >
            <div class="aa-branch-name">{{ d }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="aa-foot">
      <div class="aa-foot-status">Activo al crear</div>
      <div class="aa-foot-actions">
        <button type="button" class="aa-btn aa-btn-cancel" @click="emit('close')">Cancelar</button>
        <button
          type="button"
          class="aa-btn aa-btn-create"
          :disabled="!canSubmit || saving || uiFlags.isCreating"
          @click="submit"
        >
          {{ saving || uiFlags.isCreating ? 'Creando…' : 'Crear agente' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Compacta + responsiva (Victor 2026-06-11): ancho acotado, scroll interno,
   columnas que se apilan en móvil → no se corta en ninguna pantalla. */
.aa-modal { width: 460px; max-width: calc(100vw - 24px); max-height: min(88vh, 760px); background: linear-gradient(180deg, rgba(15,15,18,0.98), rgba(8,8,10,0.98)); border: 1px solid rgba(251,191,36,0.18); border-radius: 16px; overflow: hidden; box-shadow: 0 32px 80px rgba(0,0,0,0.70); position: relative; display: flex; flex-direction: column; color: #fff; font-family: -apple-system, system-ui, sans-serif; }
.aa-modal::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg, transparent, rgba(251,191,36,0.40), transparent); }

.aa-head { padding: 16px 20px; display: flex; align-items: center; gap: 12px; border-bottom: 1px solid rgba(255,255,255,0.06); }
.aa-avatar { width: 40px; height: 40px; border-radius: 11px; background: linear-gradient(135deg, #fbbf24, #d97706); color: #18181b; display: flex; align-items: center; justify-content: center; font-weight: 900; font-size: 14px; box-shadow: 0 4px 14px rgba(251,191,36,0.35); flex-shrink: 0; }
.aa-head-info { flex: 1; min-width: 0; }
.aa-head-title { font-size: 15px; font-weight: 800; color: #fff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.aa-head-sub { font-size: 11px; color: rgba(255,255,255,0.50); margin-top: 2px; }
.aa-head-x { width: 28px; height: 28px; border-radius: 7px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); color: rgba(255,255,255,0.50); display: flex; align-items: center; justify-content: center; font-size: 16px; cursor: pointer; line-height: 1; padding: 0; flex-shrink: 0; }
.aa-head-x:hover { background: rgba(239,68,68,0.10); color: #f87171; border-color: rgba(239,68,68,0.30); }

.aa-body { padding: 16px 20px; overflow-y: auto; flex: 1; display: flex; flex-direction: column; gap: 18px; }
.aa-sect { display: flex; flex-direction: column; gap: 9px; }

.aa-section-title { display: flex; align-items: center; gap: 8px; font-size: 9.5px; font-weight: 900; letter-spacing: 1.2px; color: #fbbf24; text-transform: uppercase; }
.aa-count { font-size: 10px; font-weight: 800; color: #18181b; background: #fbbf24; border-radius: 999px; padding: 1px 7px; }

.aa-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.aa-field { min-width: 0; }
.aa-field-label { font-size: 10.5px; color: rgba(255,255,255,0.65); margin-bottom: 4px; font-weight: 600; }
.aa-lbl-hint { color: rgba(255,255,255,0.35); font-weight: 400; }
.aa-req { color: #f87171; }
.aa-input { width: 100%; padding: 9px 11px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.10); border-radius: 8px; color: #fff; font-size: 12.5px; font-family: inherit; outline: none; transition: all 0.18s; }
.aa-input::placeholder { color: rgba(255,255,255,0.30); }
.aa-input:focus { border-color: rgba(251,191,36,0.55); background: rgba(251,191,36,0.04); }
.aa-input-error { border-color: rgba(239,68,68,0.55) !important; }

.aa-pass-wrap { position: relative; }
.aa-input-pass { padding-right: 32px; }
.aa-pass-confirm { margin-top: 8px; }
.aa-pass-toggle { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: transparent; border: none; color: rgba(255,255,255,0.40); cursor: pointer; font-size: 14px; padding: 0; }
.aa-pass-help { font-size: 10px; color: rgba(251,191,36,0.85); margin-top: 6px; line-height: 1.4; }
.aa-pass-error { color: #f87171; }
.aa-pass-ok { color: #34d399; }

/* Rol base · grilla seleccionable (12 plantillas) */
.aa-roles2 { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.aa-role2 { padding: 9px 11px; border: 1px solid rgba(255,255,255,0.10); border-radius: 9px; background: rgba(255,255,255,0.02); color: rgba(255,255,255,0.78); font-size: 11.5px; font-weight: 700; font-family: inherit; cursor: pointer; text-align: left; transition: all 0.15s; }
.aa-role2:hover { border-color: rgba(251,191,36,0.30); }
.aa-role2.on { border-color: rgba(251,191,36,0.55); background: linear-gradient(135deg, rgba(251,191,36,0.14), rgba(251,191,36,0.02)); color: #fff; box-shadow: 0 0 0 3px rgba(251,191,36,0.08); }
.aa-seg-hint { font-size: 10.5px; color: rgba(255,255,255,0.45); line-height: 1.4; }

.aa-branches { display: flex; flex-wrap: wrap; gap: 7px; }
.aa-branch-chip { padding: 6px 11px 6px 8px; border: 1px solid rgba(255,255,255,0.10); border-radius: 999px; cursor: pointer; transition: all 0.18s; display: flex; align-items: center; gap: 6px; background: rgba(255,255,255,0.02); min-width: 0; }
.aa-branch-chip:hover { background: rgba(255,255,255,0.05); }
.aa-branch-chip.active { background: rgba(251,191,36,0.10); border-color: rgba(251,191,36,0.50); }
.aa-branch-chip.active::after { content: '✓'; font-size: 10px; color: #fbbf24; font-weight: 900; margin-left: 2px; }
.aa-branch-chip.disabled { opacity: 0.4; cursor: not-allowed; }
.aa-branch-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.aa-branch-dot.pers { background: linear-gradient(135deg, #818cf8, #6366f1); }
.aa-branch-dot.lrm  { background: linear-gradient(135deg, #c084fc, #7c3aed); }
.aa-branch-dot.sde  { background: linear-gradient(135deg, #34d399, #059669); }
.aa-branch-dot.sdo  { background: linear-gradient(135deg, #60a5fa, #1d4ed8); }
.aa-branch-dot.stg  { background: linear-gradient(135deg, #fbbf24, #c2410c); }
.aa-branch-dot.other { background: linear-gradient(135deg, #94a3b8, #64748b); }
.aa-branch-name { font-size: 11px; font-weight: 700; color: rgba(255,255,255,0.80); letter-spacing: 0.2px; }
.aa-branch-chip.active .aa-branch-name { color: #fff; }
.aa-primary-tag { font-size: 8.5px; font-weight: 800; color: #18181b; background: #fbbf24; border-radius: 999px; padding: 1px 6px; margin-left: 4px; }
.aa-branches-info { font-size: 10px; color: rgba(255,255,255,0.45); }

.aa-foot { padding: 13px 20px; background: rgba(0,0,0,0.40); border-top: 1px solid rgba(255,255,255,0.06); display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.aa-foot-status { display: flex; align-items: center; gap: 6px; font-size: 10.5px; color: rgba(34,197,94,0.85); }
.aa-foot-status::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: #22c55e; box-shadow: 0 0 6px #22c55e; }
.aa-foot-actions { display: flex; gap: 8px; }
.aa-btn { padding: 9px 16px; border-radius: 8px; font-size: 12px; font-weight: 800; cursor: pointer; border: none; font-family: inherit; transition: all 0.18s; }
.aa-btn-cancel { background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.65); border: 1px solid rgba(255,255,255,0.10); }
.aa-btn-cancel:hover { background: rgba(255,255,255,0.08); color: #fff; }
.aa-btn-create { background: linear-gradient(135deg, #fbbf24, #d97706); color: #18181b; box-shadow: 0 4px 14px rgba(251,191,36,0.45); display: inline-flex; align-items: center; gap: 5px; }
.aa-btn-create:hover:not(:disabled) { filter: brightness(1.06); transform: translateY(-1px); }
.aa-btn-create:disabled { opacity: 0.45; cursor: not-allowed; }

/* Móvil / pantallas angostas: una columna para que nada se corte. */
@media (max-width: 400px) {
  .aa-grid-2 { grid-template-columns: 1fr; }
  .aa-roles2 { grid-template-columns: 1fr; }
  .aa-foot { flex-wrap: wrap; }
  .aa-foot-actions { flex: 1; }
  .aa-btn-create { flex: 1; justify-content: center; }
}
</style>
